import { createSelector, Selector } from '@reduxjs/toolkit'
import { isNil } from 'lodash-es'
import moment from 'moment'
import {
  Collateral,
  CollateralBasket,
  Deal,
  DealDetail,
  DealHistorySubscriptionIdentifier,
  DealHistoryUpdate,
  DealInterestPeriodUpdate,
  DealStatus,
  DealSubscriptionIdentifier,
  DealUpdate,
  EntityType,
  InitiatingActor,
  NegotiationFieldMeta,
  OrderTerm,
  ServicingDetails,
  ServicingSideDetails,
  ServicingSideDetailsMeta,
  ServicingType,
  TradingEntity,
  TradingEntityWithAlias,
  User,
  ValidationIssue
} from 'api'
import { GlobalState } from 'store'
import { RepositoryEntry } from 'store/repository'
import { dealHistoryStoreSelector, dealInterestPeriodStoreSelector, dealStoreSelector } from './storeSelectors'
import { isUserTradingEntityGetterSelector } from './adminSelectors'
import { createAllEntitiesSelector, createEntityGetterSelector } from './entitySelectors'
import { createUserByUsernameGetterSelector, isAdminGatewayUserSelector } from './userSelectors'
import { createTradingEntityWithAliasGetterSelector } from './tradingEntitySelectors/tradingEntitySelectors'
import { showOrgActivitySelector } from './gridSelectors'
import { createAuthorisedUserSelector } from './connectionSelectors'
import {
  dealIdForRelatedDealTradesSelector,
  dealIdForRelatedMidTradeIncomingTradesSelector,
  dealIdForRelatedMidTradeInitiatedTradesSelector,
  dealIdForRelatedOpenRepoTradesSelector,
  dealIdForRelatedStructuredTradesSelector
} from './selectionSelectors'
import { createHolidayGetterSelector } from './holidaySelectors'
import { DateLike, getCurrentWindowId, getPrevTradingDay, ParametricSelector, toSide } from 'utils'
import {
  applyUpdatesForWindow,
  createInvalidValueValidationIssue,
  createSponsorFromCounterparty,
  Severity,
  sortCreatedAtAndReplacedWithDealId
} from 'model'
import { getDefaultEffectiveDate, getIsBeforeCutOffTime } from 'view/shared/servicingDialogs/utils'
import { Locale, Translation } from 'locale'
import { getNegotiatedValue } from 'view/routes/deals/components/utils'
import { MidTradeEventFieldNames } from 'view/routes/midTradeEvents/DataGrid/MidTradeEventsColumnNames'
import { getDealInterestPeriodId, sortDealDetailsByLastUpdatedAt } from 'model/dealUtils'
import { MidTradeEventsProviderValue } from 'view/routes/midTradeEvents/Screen/MidTradeEventsViewContext'
import { MID_TRADE_VIEW_INCOMING, MID_TRADE_VIEW_INITIATED } from './midTradeEventsSelectors'

export function createDealEntrySelector(
  subscriptionIdentifiers?: (DealSubscriptionIdentifier | DealHistorySubscriptionIdentifier)[]
): Selector<GlobalState, RepositoryEntry<DealUpdate | DealInterestPeriodUpdate | DealHistoryUpdate>[]> {
  return createSelector(
    dealStoreSelector,
    dealInterestPeriodStoreSelector,
    dealHistoryStoreSelector,
    isUserTradingEntityGetterSelector,
    createAuthorisedUserSelector(),
    showOrgActivitySelector,
    (dealStore, dealInterestPeriodStore, dealHistoryStore, getIsUserTradingEntity, authorisedUser, showOrgActivity) => {
      const dealEntries = [
        ...Object.values(dealStore),
        ...Object.values(dealInterestPeriodStore),
        ...Object.values(dealHistoryStore)
      ].filter(entry => {
        if (!getIsUserTradingEntity(entry.lastUpdate.entity.fundId) && !getIsUserTradingEntity(entry.lastUpdate.entity.counterpartyId)) {
          return false
        }
        if (
          subscriptionIdentifiers &&
          subscriptionIdentifiers.length > 0 &&
          !subscriptionIdentifiers.some(subscriptionIdentifier => entry.lastUpdate.subscriptionIdentifiers.includes(subscriptionIdentifier))
        ) {
          return false
        }
        if (showOrgActivity) {
          return true
        }
        const deal = entry.lastUpdate.entity
        return deal.createdBy === authorisedUser.username
      })

      return sortCreatedAtAndReplacedWithDealId(dealEntries)
    }
  )
}

const getEffectiveField = (servicingType: ServicingType | undefined) => {
  switch (servicingType) {
    case ServicingType.TERMINATION:
    case ServicingType.RETURN_RECALL:
      return NegotiationFieldMeta.END_DATE
    case ServicingType.RERATE:
      return NegotiationFieldMeta.RERATE_DATE
    default:
      return NegotiationFieldMeta.START_DATE
  }
}

const getEffectiveFieldName = (servicingType: ServicingType | undefined) => {
  switch (servicingType) {
    case ServicingType.TERMINATION:
    case ServicingType.RETURN_RECALL:
      return MidTradeEventFieldNames.END_DATE
    case ServicingType.RERATE:
      return MidTradeEventFieldNames.RERATE_DATE
    default:
      return MidTradeEventFieldNames.START_DATE
  }
}

function getDealDetailValidationIssues(
  deal: Deal,
  servicingSideDetails: ServicingSideDetails | null,
  defaultNotificationDeadlineTime: string | null | undefined,
  holidays: DateLike[]
): ValidationIssue[] {
  const { servicingDetails, endDate, noticePeriodDays } = deal
  if (!servicingSideDetails || !servicingDetails) {
    return []
  }

  if (servicingDetails.servicingType.servicingType === ServicingType.ROLL_IN_PLACE) {
    return servicingSideDetails.validationIssues ?? []
  }

  const maxEffectiveDate = isNil(endDate) ? null : getPrevTradingDay(new Date(endDate), holidays)
  const isBeforeCutOffTime =
    !defaultNotificationDeadlineTime || getIsBeforeCutOffTime(defaultNotificationDeadlineTime, servicingDetails.createdAt)
  const defaultEffectiveDate = getDefaultEffectiveDate(
    new Date(servicingDetails.createdAt),
    isBeforeCutOffTime,
    noticePeriodDays ?? 0,
    holidays,
    maxEffectiveDate
  )

  const effectiveDateFieldMeta = getEffectiveField(deal.servicingDetails?.servicingType.servicingType || undefined)
  const effectiveDateField = servicingSideDetails.negotiationFields[effectiveDateFieldMeta]?.negotiableFieldRequired
  const effectiveDate =
    effectiveDateField && (effectiveDateField.modified ? effectiveDateField.modifiedValue : effectiveDateField.negotiated)

  return defaultEffectiveDate && !moment(defaultEffectiveDate).isSame(effectiveDate, 'day')
    ? [
        ...(servicingSideDetails.validationIssues ?? []),
        createInvalidValueValidationIssue(
          getEffectiveFieldName(deal.servicingDetails?.servicingType.servicingType || undefined),
          Locale.translate(Translation.SC_EFFECTIVE_DATE_CHANGED_FROM_DEFAULT),
          Severity.WARN
        )
      ]
    : servicingSideDetails.validationIssues ?? []
}

function getIsServicingInitiator(servicingDetails: ServicingDetails | null, isAdminUser: boolean, ownOrder: boolean) {
  if (servicingDetails === null) {
    return null
  }

  if (
    isAdminUser &&
    [
      ServicingType.TERMINATION,
      ServicingType.CLOSE_OPEN,
      ServicingType.RERATE,
      ServicingType.SERVICING_AMEND,
      ServicingType.SUBSTITUTION,
      ServicingType.SERVICING_ADMIN_CANCEL,
      null
    ].includes(servicingDetails.servicingType.servicingType)
  ) {
    return servicingDetails.servicingInitiator === InitiatingActor.ADMIN
  }

  return ownOrder
    ? servicingDetails.servicingInitiator === InitiatingActor.ORDER_INITIATOR
    : servicingDetails.servicingInitiator === InitiatingActor.ORDER_RESPONDER
}

function getOwnOrder(servicingDetails: ServicingDetails | null, isServicingInitiator: boolean | null, ownOrder: boolean) {
  if (isNil(servicingDetails) || isNil(isServicingInitiator)) {
    return ownOrder
  }

  return isServicingInitiator
    ? servicingDetails.servicingInitiator === InitiatingActor.ORDER_INITIATOR
    : servicingDetails.servicingInitiator === InitiatingActor.ORDER_RESPONDER
}

function getServicingSideDetailsMeta(
  servicingSideDetails: ServicingSideDetails | null,
  deal: Deal,
  ownOrder: boolean,
  getCollateral: (entityId: string) => Collateral | null,
  getTradingEntity: (entityId: string) => TradingEntity | null,
  getTradingEntityWithAlias: (ownerTradingEntityId: string, subjectTradingEntityId: string) => TradingEntityWithAlias | null,
  getUser: (entityId: string) => User | null
): ServicingSideDetailsMeta | null {
  if (!servicingSideDetails) {
    return null
  }

  const collateralId = getNegotiatedValue<string>(
    servicingSideDetails.negotiationFields[NegotiationFieldMeta.COLLATERAL]?.negotiableFieldRequired
  )
  const fundId = getNegotiatedValue<string>(servicingSideDetails.negotiationFields[NegotiationFieldMeta.FUND_ID]?.negotiableFieldRequired)
  const counterpartyId = getNegotiatedValue<string>(
    servicingSideDetails.negotiationFields[NegotiationFieldMeta.COUNTERPARTY_ID]?.negotiableFieldRequired
  )
  const initiatorInvestmentDecisionMakerId = getNegotiatedValue<string>(
    servicingSideDetails.sideFields[NegotiationFieldMeta.INITIATOR_INVESTMENT_DECISION_MAKER]?.negotiableFieldRequired
  )
  const initiatorExecutionDecisionMakerId = getNegotiatedValue<string>(
    servicingSideDetails.sideFields[NegotiationFieldMeta.INITIATOR_EXECUTION_DECISION_MAKER]?.negotiableFieldRequired
  )
  const responderInvestmentDecisionMakerId = getNegotiatedValue<string>(
    servicingSideDetails.sideFields[NegotiationFieldMeta.RESPONDER_INVESTMENT_DECISION_MAKER]?.negotiableFieldRequired
  )
  const responderExecutionDecisionMakerId = getNegotiatedValue<string>(
    servicingSideDetails.sideFields[NegotiationFieldMeta.RESPONDER_EXECUTION_DECISION_MAKER]?.negotiableFieldRequired
  )

  return servicingSideDetails
    ? {
        [NegotiationFieldMeta.COLLATERAL]: collateralId ? getCollateral(collateralId) : null,
        [NegotiationFieldMeta.FUND_ID]: fundId
          ? ownOrder
            ? getTradingEntity(fundId)
            : getTradingEntityWithAlias(counterpartyId ?? deal.counterpartyId, fundId)
          : null,
        [NegotiationFieldMeta.COUNTERPARTY_ID]: counterpartyId
          ? ownOrder
            ? getTradingEntityWithAlias(fundId ?? deal.fundId, counterpartyId)
            : getTradingEntity(counterpartyId)
          : null,
        [NegotiationFieldMeta.START_DATE]: getNegotiatedValue<string>(
          servicingSideDetails.negotiationFields[NegotiationFieldMeta.START_DATE]?.negotiableFieldRequired
        ),
        [NegotiationFieldMeta.END_DATE]: getNegotiatedValue<string>(
          servicingSideDetails.negotiationFields[NegotiationFieldMeta.END_DATE]?.negotiableFieldRequired
        ),
        [NegotiationFieldMeta.TERM]: getNegotiatedValue<OrderTerm>(
          servicingSideDetails.negotiationFields[NegotiationFieldMeta.TERM]?.negotiableFieldRequired
        ),
        [NegotiationFieldMeta.INITIATOR_INVESTMENT_DECISION_MAKER]: initiatorInvestmentDecisionMakerId
          ? getUser(initiatorInvestmentDecisionMakerId)
          : null,
        [NegotiationFieldMeta.INITIATOR_EXECUTION_DECISION_MAKER]: initiatorExecutionDecisionMakerId
          ? getUser(initiatorExecutionDecisionMakerId)
          : null,
        [NegotiationFieldMeta.RESPONDER_INVESTMENT_DECISION_MAKER]: responderInvestmentDecisionMakerId
          ? getUser(responderInvestmentDecisionMakerId)
          : null,
        [NegotiationFieldMeta.RESPONDER_EXECUTION_DECISION_MAKER]: responderExecutionDecisionMakerId
          ? getUser(responderExecutionDecisionMakerId)
          : null
      }
    : null
}

function isDealInterestPeriod(update: DealUpdate | DealInterestPeriodUpdate | DealHistoryUpdate): update is DealInterestPeriodUpdate {
  return update.entityType === EntityType.DEAL_INTEREST_PERIOD
}

function isDealHistory(update: DealUpdate | DealInterestPeriodUpdate | DealHistoryUpdate): update is DealHistoryUpdate {
  return update.entityType === EntityType.DEAL_HISTORY
}

function getNegotiatedSecondaryInstrumentName(
  servicingSideDetails: ServicingSideDetails | null,
  getCollateralBasket: (entityId: string) => CollateralBasket | null
) {
  if (!servicingSideDetails) {
    return null
  }

  const secondaryInstrumentId = getNegotiatedValue<string>(
    servicingSideDetails.negotiationFields[NegotiationFieldMeta.SECONDARY_BASKET_ID]?.negotiableFieldRequired
  )

  if (!secondaryInstrumentId) {
    return null
  }

  return getCollateralBasket(secondaryInstrumentId)?.name
}

export function createDealDetailsSelector(
  subscriptionIdentifiers?: (DealSubscriptionIdentifier | DealHistorySubscriptionIdentifier)[],
  forcedIsServicingInitiator?: boolean
): Selector<GlobalState, DealDetail[]> {
  return createSelector(
    createDealEntrySelector(subscriptionIdentifiers),
    isUserTradingEntityGetterSelector,
    createEntityGetterSelector(EntityType.TRADING_ENTITY),
    createTradingEntityWithAliasGetterSelector(),
    createEntityGetterSelector(EntityType.ORGANISATION),
    createUserByUsernameGetterSelector,
    createEntityGetterSelector(EntityType.CLEARING_HOUSE),
    createEntityGetterSelector(EntityType.COLLATERAL),
    createEntityGetterSelector(EntityType.COLLATERAL_BASKET),
    createEntityGetterSelector(EntityType.PRODUCT),
    createEntityGetterSelector(EntityType.PRODUCT_SECTOR),
    createEntityGetterSelector(EntityType.REFERENCE_RATE),
    createEntityGetterSelector(EntityType.COLLECTION),
    createEntityGetterSelector(EntityType.SPONSOR),
    createEntityGetterSelector(EntityType.USER),
    createEntityGetterSelector(EntityType.DEAL_INTEREST_PERIOD),
    createEntityGetterSelector(EntityType.NOTES),
    createHolidayGetterSelector,
    dealIdForRelatedDealTradesSelector,
    dealIdForRelatedOpenRepoTradesSelector,
    isAdminGatewayUserSelector,
    (
      dealEntries,
      getIsUserTradingEntity,
      getTradingEntity,
      getTradingEntityWithAlias,
      getOrganisation,
      getUserByUsername,
      getClearingHouse,
      getCollateral,
      getCollateralBasket,
      getProduct,
      getProductSector,
      getReferenceRate,
      getCollection,
      getSponsor,
      getUser,
      getDealInterestPeriod,
      getNotes,
      getHoliday,
      dealIdForRelatedDealTrades,
      dealIdForRelatedOpenRepoTrades,
      isAdminUser
    ) => {
      return dealEntries.map(dealEntry => {
        const deal: Deal = applyUpdatesForWindow(dealEntry, getCurrentWindowId())

        function getDealTradingEntityWithAlias(id: string) {
          const entity = getTradingEntity(id)
          const alias = deal.counterpartyAlias

          return entity ? { ...entity, alias } : null
        }

        const modified = dealEntry.fieldUpdates.some(update => update.meta.sourceWindowId === getCurrentWindowId())

        const availableEvents =
          !isDealInterestPeriod(dealEntry.lastUpdate) && !isDealHistory(dealEntry.lastUpdate) ? dealEntry.lastUpdate.availableEvents : []
        const ownOrder = !isNil(forcedIsServicingInitiator)
          ? getOwnOrder(deal.servicingDetails, forcedIsServicingInitiator, getIsUserTradingEntity(deal.fundId))
          : getIsUserTradingEntity(deal.fundId)

        const isServicingInitiator = forcedIsServicingInitiator ?? getIsServicingInitiator(deal.servicingDetails, isAdminUser, ownOrder)
        const servicingSideDetails = deal.servicingDetails
          ? isServicingInitiator
            ? deal.servicingDetails.servicingInitiatorSideDetails
            : deal.servicingDetails.servicingResponderSideDetails
          : null
        const servicingSideDetailsMeta = getServicingSideDetailsMeta(
          servicingSideDetails,
          deal,
          ownOrder,
          getCollateral,
          getTradingEntity,
          getTradingEntityWithAlias,
          getUser
        )
        const userWithServicingLock = servicingSideDetails?.userWithLock ? getUserByUsername(servicingSideDetails.userWithLock) : null

        const dealInterestPeriod =
          deal.committedServicingType === ServicingType.RERATE && isDealHistory(dealEntry.lastUpdate)
            ? getDealInterestPeriod(getDealInterestPeriodId(deal))
            : null

        const orderFund = deal.fundId
          ? ownOrder
            ? getTradingEntity(deal.fundId)
            : getTradingEntityWithAlias(deal.counterpartyId, deal.fundId)
          : null
        const orderFundFirm = orderFund ? getOrganisation(orderFund.organisationId) : null
        const counterparty = deal.counterpartyId
          ? ownOrder
            ? getDealTradingEntityWithAlias(deal.counterpartyId)
            : getTradingEntity(deal.counterpartyId)
          : null
        const counterpartyFirm = counterparty ? getOrganisation(counterparty.organisationId) : null

        const clearingHouse = deal.clearingHouseId ? getClearingHouse(deal.clearingHouseId) : null
        const sponsor = deal.sponsorId
          ? deal.isSponsorCounterparty
            ? createSponsorFromCounterparty(counterparty)
            : getSponsor(deal.sponsorId)
          : null
        const counterpartySponsor = deal.counterpartySponsorId ? getSponsor(deal.counterpartySponsorId) : null

        const collateral = deal.collateralId ? getCollateral(deal.collateralId) : null
        const parentCollateralBasket = deal.parentDealBasketId ? getCollateralBasket(deal.parentDealBasketId) : null
        const collateralBasket = deal.basketAllocation ? getCollateralBasket(deal.basketAllocation.basketId) : null
        const collateralBasketName = parentCollateralBasket ? parentCollateralBasket.name : collateralBasket ? collateralBasket.name : null
        const productSector = deal.productSectorId ? getProductSector(deal.productSectorId) : null
        const product = deal.productId ? getProduct(deal.productId) : null
        const referenceRate = deal.referenceRate ? getReferenceRate(deal.referenceRate) : null
        const floatCrystallisationDays = deal.floatCrystallisationDays
        const collection = deal.collectionId ? getCollection(deal.collectionId) : null
        const containerCollection = deal.containerCollectionId ? getCollection(deal.containerCollectionId) : null

        const axeTradeCondition = {
          isAxed: deal.isAxed,
          tradeCondition: deal.tradeConditionFlag
        }

        const holidays = collateral ? getHoliday(collateral.currency)?.holidays ?? [] : []
        const notes = deal.notesId ? getNotes(deal.notesId) : null

        const backEndValidationIssues =
          !isDealInterestPeriod(dealEntry.lastUpdate) && !isDealHistory(dealEntry.lastUpdate) ? dealEntry.lastUpdate.validationIssues : []

        const validationIssues = [
          ...getDealDetailValidationIssues(deal, servicingSideDetails, productSector?.defaultNotificationDeadlineTime, holidays),
          ...backEndValidationIssues
        ]

        const initiatorExecutionDecisionMaker = deal.initiatorExecutionDecisionMaker ? getUser(deal.initiatorExecutionDecisionMaker) : null
        const initiatorInvestmentDecisionMaker = deal.initiatorInvestmentDecisionMaker
          ? getUser(deal.initiatorInvestmentDecisionMaker)
          : null
        const responderExecutionDecisionMaker = deal.responderExecutionDecisionMaker ? getUser(deal.responderExecutionDecisionMaker) : null
        const responderInvestmentDecisionMaker = deal.responderInvestmentDecisionMaker
          ? getUser(deal.responderInvestmentDecisionMaker)
          : null

        const primaryCoverUser = toSide({
          ownOrder,
          initiator: getUser(deal.fundPrimaryCoverUserId),
          responder: getUser(deal.counterpartyPrimaryCoverUserId)
        })

        const secondaryCoverUser = toSide({
          ownOrder,
          initiator: getUser(deal.fundSecondaryCoverUserId),
          responder: getUser(deal.counterpartySecondaryCoverUserId)
        })

        const isSelectedForRelatedDealTrades = deal.dealId === dealIdForRelatedDealTrades
        const isSelectedForRelatedOpenRepoTrades = deal.dealId === dealIdForRelatedOpenRepoTrades

        const secondaryInstrumentName = deal.secondaryBasketId
          ? getCollateralBasket(deal.secondaryBasketId)?.name ?? null
          : deal.secondaryCollateralId
          ? getCollateral(deal.secondaryCollateralId)?.name ?? null
          : null
        const negotiatedSecondaryInstrumentName = getNegotiatedSecondaryInstrumentName(servicingSideDetails, getCollateralBasket) ?? null

        const myEntityShortName = toSide({
          ownOrder,
          initiator: orderFund?.shortName,
          responder: counterparty?.shortName
        })

        const ficcShortName = toSide({
          ownOrder,
          initiator: orderFund?.ficcShortName,
          responder: counterparty?.ficcShortName
        })

        return {
          availableEvents,
          axeTradeCondition,
          clearingHouse,
          collateral,
          collateralBasketName,
          collection,
          containerCollection,
          counterparty,
          counterpartyFirm,
          counterpartySponsor,
          collateralBasket,
          deal,
          dealInterestPeriod,
          direction: deal.direction,
          floatCrystallisationDays,
          initiatorExecutionDecisionMaker,
          initiatorInvestmentDecisionMaker,
          isServicingInitiator,
          isSelectedForRelatedDealTrades,
          isSelectedForRelatedOpenRepoTrades,
          isDealInterestPeriod: isDealInterestPeriod(dealEntry.lastUpdate),
          isDealHistory: isDealHistory(dealEntry.lastUpdate),
          modified,
          negotiatedSecondaryInstrumentName,
          notes,
          orderFund,
          orderFundFirm,
          ownOrder,
          parentCollateralBasket,
          primaryCoverUser,
          productSector,
          product,
          referenceRate,
          responderExecutionDecisionMaker,
          responderInvestmentDecisionMaker,
          secondaryCoverUser,
          secondaryInstrumentName,
          servicingSideDetails,
          servicingSideDetailsMeta,
          sponsor,
          userWithServicingLock,
          validationIssues,
          myEntityShortName,
          ficcShortName
        }
      })
    }
  )
}

export function createDealDetailSelector<TProps>(
  dealIdSelector: ParametricSelector<{}, TProps, string | null>
): ParametricSelector<GlobalState, TProps, DealDetail | undefined> {
  return createSelector([createDealDetailsSelector(), dealIdSelector], (dealDetails, dealId) => {
    return dealDetails.find(dealDetail => dealId === dealDetail.deal.dealId)
  })
}

export function createDealInterestPeriodDetailSelector<TProps>(
  dealIdSelector: ParametricSelector<{}, TProps, string | null>,
  dealVersionSelector: ParametricSelector<{}, TProps, number | null>
): ParametricSelector<GlobalState, TProps, DealDetail | undefined> {
  return createSelector(
    [createDealDetailsSelector([DealSubscriptionIdentifier.DEAL_INTEREST_PERIOD]), dealIdSelector, dealVersionSelector],
    (dealDetails, dealId, version) => {
      return dealDetails.find(dealDetail => dealId === dealDetail.deal.dealId && version === dealDetail.deal.version)
    }
  )
}

export function createFilteredDealDetailsSelector<TProps>(
  dealIdsSelector: ParametricSelector<{}, TProps, string[]>
): ParametricSelector<GlobalState, TProps, DealDetail[]> {
  return createSelector(
    createDealDetailsSelector([
      DealSubscriptionIdentifier.BLOTTER,
      DealSubscriptionIdentifier.OPEN_REPO,
      DealSubscriptionIdentifier.ROLLS,
      DealSubscriptionIdentifier.STRUCTURED
    ]),
    dealIdsSelector,
    (dealDetails, dealIds) => {
      return dealDetails.filter(dealDetail => dealIds.includes(dealDetail.deal.dealId) && !dealDetail.isDealHistory)
    }
  )
}

export const createDealDetailsByIdsSelector: Selector<GlobalState, (dealIds: string[]) => DealDetail[]> = createSelector(
  createDealDetailsSelector([
    DealSubscriptionIdentifier.BLOTTER,
    DealSubscriptionIdentifier.OPEN_REPO,
    DealSubscriptionIdentifier.ROLLS,
    DealSubscriptionIdentifier.STRUCTURED
  ]),
  dealDetails => (dealIds: string[]) => {
    return dealDetails.filter(dealDetail => dealIds.includes(dealDetail.deal.dealId) && !dealDetail.isDealHistory)
  }
)

export const dealDetailGetterSelector: Selector<GlobalState, (dealId: string) => DealDetail | null> = createSelector(
  [createDealDetailsSelector()],
  dealDetails => dealId => dealDetails.find(dealDetail => dealId === dealDetail.deal.dealId) ?? null
)

export const adminClearingAlertSelector = createSelector(
  createAllEntitiesSelector(EntityType.DEAL),
  isAdminGatewayUserSelector,
  (deals, isAdmin) => {
    if (!isAdmin) {
      return false
    }

    return deals.some(deal => deal.status === DealStatus.CLEARING_FAILED)
  }
)

export const someDealsWithinWarningPeriodSelector: Selector<GlobalState, boolean> = createSelector(createDealDetailsSelector(), deals =>
  deals.some(dealDetail => dealDetail.deal.basketAllocation?.withinWarningPeriod)
)

const filterForRelatedDeals = (dealDetails: DealDetail[], selectedDeal?: DealDetail) => {
  if (isNil(selectedDeal)) {
    return []
  }

  return dealDetails.filter(
    dealDetail =>
      (!dealDetail.isDealInterestPeriod && dealDetail.deal.dealId === selectedDeal.deal.dealId) ||
      dealDetail.deal.originalDealId === selectedDeal.deal.originalDealId
  )
}

export const relatedDealDetailsSelector: Selector<GlobalState, DealDetail[]> = createSelector(
  [createDealDetailsSelector([DealSubscriptionIdentifier.DEAL_HISTORY]), createDealDetailSelector(dealIdForRelatedDealTradesSelector)],
  (dealInterestingPeriodDeals, selectedDeal) =>
    sortDealDetailsByLastUpdatedAt(filterForRelatedDeals(dealInterestingPeriodDeals, selectedDeal))
)

export const relatedOpenRepoDetailsSelector: Selector<GlobalState, DealDetail[]> = createSelector(
  [createDealDetailsSelector([DealSubscriptionIdentifier.DEAL_HISTORY]), createDealDetailSelector(dealIdForRelatedOpenRepoTradesSelector)],
  (dealInterestingPeriodDeals, selectedDeal) =>
    sortDealDetailsByLastUpdatedAt(filterForRelatedDeals(dealInterestingPeriodDeals, selectedDeal))
)

export const relatedStructuredDetailsSelector: Selector<GlobalState, DealDetail[]> = createSelector(
  [
    createDealDetailsSelector([DealSubscriptionIdentifier.DEAL_HISTORY]),
    createDealDetailSelector(dealIdForRelatedStructuredTradesSelector)
  ],
  (dealInterestingPeriodDeals, selectedDeal) =>
    sortDealDetailsByLastUpdatedAt(filterForRelatedDeals(dealInterestingPeriodDeals, selectedDeal))
)

const relatedMidTradeEventsInitiatedSelector: Selector<GlobalState, DealDetail[]> = createSelector(
  [
    createDealDetailsSelector([DealSubscriptionIdentifier.DEAL_HISTORY]),
    createDealDetailSelector(dealIdForRelatedMidTradeInitiatedTradesSelector)
  ],
  (dealInterestingPeriodDeals, selectedDeal) =>
    sortDealDetailsByLastUpdatedAt(filterForRelatedDeals(dealInterestingPeriodDeals, selectedDeal))
)

const relatedMidTradeEventsIncomingSelector: Selector<GlobalState, DealDetail[]> = createSelector(
  [
    createDealDetailsSelector([DealSubscriptionIdentifier.DEAL_HISTORY]),
    createDealDetailSelector(dealIdForRelatedMidTradeIncomingTradesSelector)
  ],
  (dealInterestingPeriodDeals, selectedDeal) =>
    sortDealDetailsByLastUpdatedAt(filterForRelatedDeals(dealInterestingPeriodDeals, selectedDeal))
)

export const relatedMidTradeEventsSelector: Selector<GlobalState, DealDetail[], [MidTradeEventsProviderValue]> = createSelector(
  [relatedMidTradeEventsInitiatedSelector, relatedMidTradeEventsIncomingSelector, (_, view: MidTradeEventsProviderValue) => view],
  (initiatedDetails, incomingDetails, view) => {
    if (view === MID_TRADE_VIEW_INITIATED) return initiatedDetails
    else if (view === MID_TRADE_VIEW_INCOMING) return incomingDetails
    return []
  }
)
